/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-constant-condition */
import { useEffect } from "react";
import * as yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Grid, Typography } from "@mui/material";

import { useForm } from "react-hook-form";
import { AxiosError } from "axios";
import useAppTranslations from "../hooks/useAppTranslations";

import Landing from "../components/Landing";

import Charge from "../components/charge/Charge";
import { IGenericResponse, IUserRegisterForm } from "../types/forms";
import { IChargeForm } from "../components/charge/data";
import useYupFormResolver from "../hooks/useYupFormResolver";
import useIugu from "../hooks/useIugu";
import api from "../services/api";
import { getCardFlag } from "../components/band-credit-card/data";
import useCustomSnackBar from "../hooks/useCustomSnackBar";

const formInitialState: IChargeForm = {
  cardCVC: "",
  cardDate: "",
  cardNumber: "",
  cardFullName: ""
};

export default function ChargePage() {
  const { t } = useAppTranslations();
  const navigate = useNavigate();
  const location = useLocation();
  const { toastMessage } = useCustomSnackBar();
  const data = location.state.data as IUserRegisterForm;
  const iuguPayment = useIugu("3147F27ED39C489B853BA712B1E9E252");

  useEffect(() => {
    if (iuguPayment) iuguPayment?.setTestMode(false);
  }, [iuguPayment]);

  const yupFormValidation = yup.object({}).shape({
    cardNumber: yup.string().required("O número do cartão é obrigatório"),
    cardFullName: yup.string().required("O nome do titular é obrigatório"),
    cardDate: yup
      .string()
      .min(7, "Data de expiração precisa ser válida")
      .required("Data de expiração é obrigatória"),
    cardCVC: yup
      .string()
      .min(3, "CVC tem que conter no mínimo 3 dígitos")
      .required("CVC é obrigatório")
  });

  const yupFormResolver = useYupFormResolver<typeof yupFormValidation>(yupFormValidation);

  const formHook = useForm<IChargeForm>({
    resolver: yupFormResolver,
    values: {
      ...formInitialState
    },
    mode: "onBlur"
  });

  const registerForm = async (dataForm: IChargeForm) => {
    const resp = await iuguPayment?.createPaymentToken({
      number: dataForm.cardNumber,
      verification_value: dataForm.cardCVC,
      first_name: dataForm.cardFullName?.split(" ").at(0),
      last_name: dataForm.cardFullName?.split(" ").slice(1).join(" "),
      month: dataForm.cardDate?.split("/").at(0),
      year: dataForm.cardDate?.split("/").at(1)
    });

    if (resp.errors) {
      toastMessage("Informações enviadas para Iugu estão inválidas");
      return;
    }

    data.payment = {
      cardBrand: getCardFlag(dataForm.cardNumber ?? ""),
      cardExpirationDate: dataForm.cardDate ?? "",
      cardFullName: dataForm.cardFullName ?? "",
      cardNumber: resp.extra_info.display_number ?? "",
      cardToken: resp.id,
      paymentTypeId: 1
    };

    data.companyCnpj = data.companyCnpj.replaceAll(/[^0-9]/gi, "");
    data.userPhone = data.userPhone.replaceAll(/[^0-9]/gi, "");

    try {
      const response = await api.postRegisterCompanyUser(data);
      if (response.data.success) {
        navigate("/thanks");
      }
    } catch (e: any) {
      const error = e as AxiosError<IGenericResponse<any>>;

      if (error.response) toastMessage(error.response.data.message);
      else toastMessage("Erro de comunicação com o serviço do Register");
    }
  };

  return (
    <Landing
      headerTitle={t("title:charge_start_trial")}
      submitButtonLabel={t("action:create_and_start_now")}
      handleCardFormSubmit={formHook.handleSubmit(registerForm)}
      submitLoading={formHook.formState.isSubmitting}
      submitButtonDisabled={formHook.formState.isSubmitting}
      CardFormContent={
        <Box
          className="rounded-2xl"
          sx={theme => ({
            py: 3,
            px: 4,
            background: theme.palette.background.default,
            border: `solid 1px ${theme.palette.grey[300]}`,

            [theme.breakpoints.down("sm")]: {
              border: "none",
              py: 1.5,
              px: 2
            }
          })}
        >
          <Grid
            container
            spacing={4}
            sx={theme => ({
              flexDirection: "row",

              [theme.breakpoints.down("sm")]: {
                flexDirection: "column"
              }
            })}
          >
            <Grid item xs={12} md={4}>
              <Typography
                fontSize={24}
                fontWeight={600}
                fontStyle="normal"
                sx={{
                  color: "#1A99FF"
                }}
              >
                {/* <Translate src="form:description:corp_form" /> */}
                Olá
              </Typography>

              <Typography fontSize={16} fontWeight={400} fontStyle="normal">
                {t("form:description:charge_desc", { companyName: null })}
              </Typography>
            </Grid>

            <Charge data={null} formHook={formHook} />
          </Grid>
        </Box>
      }
    />
  );
}
